import { useTheme } from "next-themes"
import { useEffect, useRef } from "react"
import { Toaster as Sonner, toast } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  // Sonner 1.5 doesn't expose a per-toast onClick option, and toast IDs
  // aren't stamped onto the DOM. Use a delegated click listener: a click
  // anywhere inside a [data-sonner-toast] (excluding inner buttons like
  // the action/close) dismisses all currently visible toasts. Sonner's
  // visible stack is small and auto-dismissing, so this is acceptable UX.
  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest("button")) return
      if (!target.closest("[data-sonner-toast]")) return
      // Defer to next frame so the click handler returns and the browser can
      // paint the "data-removed" state before any subsequent main-thread work
      // (e.g. navigation, lazy chunk load) starves the exit transition.
      requestAnimationFrame(() => toast.dismiss())
    }
    root.addEventListener("click", handler)
    return () => root.removeEventListener("click", handler)
  }, [])

  return (
    <div ref={containerRef}>
      <Sonner
        theme={theme as ToasterProps["theme"]}
        className="toaster group"
        toastOptions={{
          classNames: {
            toast:
              "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg cursor-pointer",
            description: "group-[.toast]:text-muted-foreground",
            actionButton:
              "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            cancelButton:
              "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          },
        }}
        {...props}
      />
    </div>
  )
}

export { Toaster, toast }
